import React from "react";
import {
  LinkOutlined,
  GlobalOutlined,
  PictureOutlined,
  VideoCameraOutlined,
  FilePdfOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";

const TemplatePreview = ({
  components,
  sampleValues = {},
  compact = false,
  mediaObj = null,
}) => {
  // Safe parsing of components
  let parsedComponents = components;
  if (typeof components === "string") {
    try {
      parsedComponents = JSON.parse(components);
    } catch (e) {
      parsedComponents = [];
    }
  }

  const headerComponent = parsedComponents?.find((c) => c.type === "HEADER");
  const bodyComponent = parsedComponents?.find((c) => c.type === "BODY");
  const footerComponent = parsedComponents?.find((c) => c.type === "FOOTER");
  const buttonsComponent = parsedComponents?.find((c) => c.type === "BUTTONS");

  // Format Header
  let formattedHeader = null;
  if (
    (!headerComponent?.format || headerComponent?.format === "TEXT") &&
    headerComponent?.text
  ) {
    formattedHeader = headerComponent.text;
    const matches = formattedHeader.match(/\{\{(\d+)\}\}/g);
    if (matches) {
      matches.forEach((match) => {
        const varIndex = match.replace(/[{}]/g, "");
        const val =
          typeof sampleValues.header === "object"
            ? sampleValues.header?.[varIndex] ||
              sampleValues.header?.["1"] ||
              `[Header Var ${varIndex}]`
            : sampleValues.header || `[Header Var ${varIndex}]`;
        formattedHeader = formattedHeader.replace(match, val);
      });
    }
  }

  const renderHeaderMediaPreview = () => {
    const format = headerComponent?.format;
    if (!format || format === "TEXT") return null;

    const mediaUrl =
      mediaObj?.previewUrl ||
      mediaObj?.mediaUrl ||
      mediaObj?.fileUrl ||
      mediaObj?.url ||
      headerComponent?.mediaUrl ||
      headerComponent?.example?.header_handle?.[0];

    const fileName =
      mediaObj?.fileName ||
      mediaObj?.originalFilename ||
      mediaObj?.name ||
      mediaObj?.filename ||
      "Document.pdf";

    if (format === "IMAGE") {
      if (
        mediaUrl &&
        typeof mediaUrl === "string" &&
        (mediaUrl.startsWith("http") ||
          mediaUrl.startsWith("blob:") ||
          mediaUrl.startsWith("data:"))
      ) {
        return (
          <div className="tw-w-full tw-h-36 tw-overflow-hidden tw-bg-gray-100 tw-relative">
            <img
              src={mediaUrl}
              alt="Header Preview"
              className="tw-w-full tw-h-full tw-object-cover"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          </div>
        );
      }
      return (
        <div className="tw-w-full tw-h-28 tw-bg-slate-100 tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-slate-400 tw-p-3">
          <PictureOutlined className="tw-text-2xl tw-mb-1 tw-text-slate-400" />
          <span className="tw-text-[11px] tw-font-semibold tw-text-slate-500">
            Image Header
          </span>
        </div>
      );
    }

    if (format === "VIDEO") {
      if (
        mediaUrl &&
        typeof mediaUrl === "string" &&
        (mediaUrl.startsWith("http") ||
          mediaUrl.startsWith("blob:") ||
          mediaUrl.startsWith("data:"))
      ) {
        return (
          <div className="tw-w-full tw-h-36 tw-bg-slate-900 tw-relative tw-flex tw-items-center tw-justify-center tw-overflow-hidden">
            <video
              src={mediaUrl}
              className="tw-w-full tw-h-full tw-object-cover tw-opacity-60"
            />
            <div className="tw-absolute tw-w-10 tw-h-10 tw-rounded-full tw-bg-white/80 tw-flex tw-items-center tw-justify-center tw-text-slate-800 tw-shadow-md">
              <PlayCircleOutlined className="tw-text-xl tw-text-emerald-600" />
            </div>
          </div>
        );
      }
      return (
        <div className="tw-w-full tw-h-28 tw-bg-slate-900 tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-white tw-p-3">
          <VideoCameraOutlined className="tw-text-2xl tw-mb-1 tw-text-emerald-400" />
          <span className="tw-text-[11px] tw-font-semibold tw-text-slate-300">
            Video Header
          </span>
        </div>
      );
    }

    if (format === "DOCUMENT") {
      return (
        <div className="tw-bg-slate-50 tw-p-3 tw-flex tw-items-center tw-gap-2.5">
          <div className="tw-w-9 tw-h-9 tw-rounded-lg tw-bg-red-50 tw-text-red-500 tw-border tw-border-solid tw-border-red-100 tw-flex tw-items-center tw-justify-center tw-shrink-0">
            <FilePdfOutlined className="tw-text-lg" />
          </div>
          <div className="tw-flex tw-flex-col tw-min-w-0 tw-flex-1">
            <span className="tw-text-[11.5px] tw-font-semibold tw-text-gray-800 tw-truncate">
              {fileName}
            </span>
            <span className="tw-text-[9.5px] tw-text-gray-400 tw-uppercase">
              Document
            </span>
          </div>
        </div>
      );
    }

    return null;
  };

  // Format Body
  let formattedBody = bodyComponent?.text || "";
  if (formattedBody) {
    const matches = formattedBody.match(/\{\{(\d+)\}\}/g);
    if (matches) {
      matches.forEach((match) => {
        const varIndex = match.replace(/[{}]/g, "");
        const val = sampleValues.body?.[varIndex] || `[Var ${varIndex}]`;
        formattedBody = formattedBody.replace(match, val);
      });
    }
  }

  const innerPhone = (
    <div
      className={`tw-mx-auto tw-relative ${
        compact
          ? "tw-w-[260px] tw-h-[440px] tw-rounded-[34px] tw-border-[6px]"
          : "tw-w-[275px] tw-h-[500px] tw-rounded-[40px] tw-border-[8px]"
      } tw-border-solid tw-border-[#111111] tw-bg-[#EFEAE2] tw-shadow-[0_15px_40px_rgba(0,0,0,0.12)] tw-flex tw-flex-col`}
    >
      {/* Dynamic Island */}
      <div className="tw-absolute tw-left-1/2 tw-top-2 tw-h-[14px] tw-w-[65px] -tw-translate-x-1/2 tw-rounded-full tw-bg-black tw-z-30" />

      <div
        className={`tw-relative tw-flex-1 tw-flex tw-flex-col tw-overflow-hidden ${
          compact ? "tw-rounded-[26px]" : "tw-rounded-[32px]"
        } tw-bg-[#EFEAE2]`}
      >
        {/* Status Bar */}
        <div className="tw-flex tw-justify-between tw-items-center tw-px-4 tw-pt-2.5 tw-pb-1 tw-bg-[#008069] tw-text-white tw-text-[9px] tw-font-medium tw-z-20">
          <span>9:41</span>
          <div className="tw-flex tw-items-center tw-gap-1">
            <span>📶</span>
            <span>100%</span>
          </div>
        </div>

        {/* WhatsApp Header Bar */}
        <div className="tw-flex tw-items-center tw-justify-between tw-bg-[#008069] tw-px-3 tw-py-1.5 tw-text-white tw-z-20 tw-shadow-sm">
          <div className="tw-flex tw-items-center tw-gap-1.5">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
            <div className="tw-w-6 tw-h-6 tw-rounded-full tw-bg-white/90 tw-flex tw-items-center tw-justify-center tw-text-gray-400 tw-overflow-hidden tw-border tw-border-solid tw-border-white/10">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
            </div>
            <div className="tw-text-[11.5px] tw-font-bold tw-truncate">
              WhatsApp Template
            </div>
          </div>
        </div>

        {/* Chat Messages Area */}
        <div className="tw-flex-1 tw-bg-[#E5DDD5] tw-p-3 tw-overflow-y-auto tw-flex tw-flex-col tw-justify-center">
          {/* WhatsApp Template Message Bubble */}
          <div className="tw-w-full tw-bg-white tw-rounded-[10px] tw-shadow-[0_1px_2px_rgba(0,0,0,0.15)] tw-overflow-hidden tw-border tw-border-solid tw-border-gray-200/40">
            {headerComponent?.format && headerComponent.format !== "TEXT" ? (
              renderHeaderMediaPreview()
            ) : formattedHeader ? (
              <div className="tw-bg-[#F8F9FA] tw-px-3 tw-py-2 tw-flex tw-items-center tw-gap-1.5">
                <span className="tw-text-[12.5px] tw-font-bold tw-[#2E3133]">
                  {formattedHeader}
                </span>
              </div>
            ) : null}

            <div className="tw-p-3 tw-text-[12px] tw-leading-[1.4] tw-text-[#2B2B2B] tw-whitespace-pre-line tw-break-words">
              {formattedBody || (
                <span className="tw-text-gray-400 tw-italic">
                  Empty template body
                </span>
              )}
              {footerComponent?.text && (
                <div className="tw-mt-2.5 tw-text-[10px] tw-text-[#8C9196] tw-font-normal">
                  {footerComponent.text}
                </div>
              )}
            </div>

            {buttonsComponent?.buttons?.length > 0 && (
              <div className="tw-flex tw-flex-col">
                {buttonsComponent.buttons.map((btn, i) => (
                  <button
                    key={i}
                    type="button"
                    className="tw-w-full tw-py-2 tw-text-center tw-text-[11.5px] tw-font-bold tw-text-blue-500 tw-bg-white tw-border-t tw-border-b-0 tw-border-r-0 tw-border-l-0 tw-border-solid tw-border-gray-100 tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-outline-none"
                  >
                    {btn.type === "URL" ? (
                      <GlobalOutlined className="tw-text-blue-500" />
                    ) : (
                      <LinkOutlined className="tw-text-blue-500" />
                    )}
                    {btn.text}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (compact) {
    return innerPhone;
  }

  return (
    <div className="tw-w-full tw-h-full tw-rounded-[24px] tw-border tw-border-solid tw-border-[#E2F0F9] tw-bg-[linear-gradient(180deg,#F0F9FF_0%,#ECFEFF_100%)] tw-p-4 tw-flex tw-flex-col tw-items-center tw-justify-center tw-shadow-[0_10px_30px_rgba(8,145,178,0.04)] tw-overflow-hidden">
      <div className="tw-text-center tw-text-[11px] tw-font-bold tw-tracking-[0.5em] tw-text-[#7C8087] tw-uppercase tw-mb-3">
        PREVIEW
      </div>
      {innerPhone}
    </div>
  );
};

export default TemplatePreview;
